// Water temperature for fish catches. Your own reading always wins. Otherwise it is filled in from
//  - the nearest Syke lake station's daily surface temperature (open data, CC BY 4.0), or
//  - Open-Meteo's sea model for spots at sea.
import type { Catch, Water } from "../shared/model.ts";
import { dbGet, dbPut } from "./db.ts";
import { t } from "./i18n.ts";
import { upgrade } from "./species.ts";
import { dayKey, distanceKm, isoDay } from "./util.ts";

// Syke stations that report daily surface water temperature: [id, name, lat, lon]. Fetched 19 Oct 2026.
const WATER_STATIONS: [id: number, name: string, lat: number, lon: number][] = [[1711,"Kevojärvi, Kevoniemi",69.7558,27.0075],[1708,"Kilpisjärvi",69.0492,20.7936],[1716,"Inari, Nellim",68.8492,28.3047],[2608,"Ounasjärvi, Enontekiö",68.3806,23.6153],[2609,"Unari, Sodankylä",67.1364,25.7456],[1717,"Oulankajoki",66.3689,29.3153],[1709,"Tornionjoki, Kukkolankoski",65.9775,24.0597],[1705,"Oijärvi",65.6217,25.9342],[2607,"Pesiöjärvi, Suomussalmi",64.9319,28.7503],[1700,"Oulujärvi, Manamansalo",64.4019,27.1692],[1699,"Nuasjärvi, Vuokatti",64.1631,28.2422],[1658,"Pielinen, Nurmes",63.5414,29.1347],[1696,"Lappajärvi, Halkosaari",63.2603,23.6356],[1672,"Pielavesi, Säviä",63.1986,26.6731],[1661,"Kallavesi, Kuopio",62.8989,27.7347],[2600,"Pääjärvi, Karstula",62.87,24.7794],[1720,"Hinjärvi, Närpiö",62.6853,21.3511],[2601,"Konnevesi, etelä",62.6161,26.3528],[1660,"Pielisjoki, Joensuu",62.5983,29.7706],[1687,"Kitusjärvi, Virrat",62.2772,24.0556],[1664,"Haukivesi, Oravi",62.1128,28.6106],[2603,"Kyyvesi, Haukivuori",62.0122,27.1956],[1694,"Näsijärvi, Kyrönlahti",61.7117,23.5547],[1673,"Jääsjärvi, Hartola",61.5661,26.0497],[1674,"Päijänne, Sysmä",61.4783,25.6308],[3094,"Päijänne, Päijätsalo",61.4675,25.59],[2604,"Längelmävesi, Kaivanto",61.4178,24.1506],[2602,"Ala-Rieveli, Heinola",61.3353,26.2044],[1685,"Säkylän Pyhäjärvi",61.1064,22.1747],[1667,"Saimaa, Lauritsala",61.0792,28.2764],[2605,"Pääjärvi, Lammi",61.0517,25.13],[2606,"Kuivajärvi, Saari, Tammela",60.7817,23.8417],[2648,"Tuusulanjärvi",60.4142,25.0397],[3081,"Lohjanjärvi",60.2231,23.8753]];
/** A station closer than this is trusted over the sea model; up to FAR_KM it's used when the sea model has nothing. */
const NEAR_KM = 15, FAR_KM = 40;
const SYKE_API = "https://rajapinnat.ymparisto.fi/api/Hydrologiarajapinta/1.1/odata/";

const kmBetween = distanceKm;
interface Station { id: number; name: string; km: number }
function nearestStation(lat: number, lon: number): Station {
  let best: Station | null = null;
  for (const [id, name, la, lo] of WATER_STATIONS) { const km = kmBetween(lat, lon, la, lo); if (!best || km < best.km) best = { id, name, km }; }
  return best!;
}

async function stationTemp(st: Station, time: number): Promise<number | null> {
  // daily values; take the one closest to the catch day (stations sometimes skip a day)
  const f = `Paikka_Id eq ${st.id} and Aika ge datetime'${dayKey(time - 2 * 864e5)}T00:00:00' and Aika le datetime'${dayKey(time + 864e5)}T00:00:00'`;
  const r = await fetch(SYKE_API + "LampoPintavesi?$filter=" + encodeURIComponent(f));
  if (!r.ok) throw new Error("syke " + r.status);
  const day = Date.parse(dayKey(time) + "T00:00:00");
  let best: { val: number; d: number } | null = null;
  for (const v of ((await r.json()).value ?? []) as { Arvo: string; Aika: string }[]) {
    const val = parseFloat(v.Arvo), d = Math.abs(Date.parse(v.Aika) - day);
    if (Number.isFinite(val) && (!best || d < best.d)) best = { val, d };
  }
  return best ? best.val : null;
}
async function seaTemp(lat: number, lon: number, time: number): Promise<number | null> {
  const q = new URLSearchParams({ latitude: lat.toFixed(4), longitude: lon.toFixed(4), hourly: "sea_surface_temperature", timezone: "GMT",
    start_date: isoDay(time - 36e5), end_date: isoDay(time + 36e5) });
  const r = await fetch("https://marine-api.open-meteo.com/v1/marine?" + q);
  if (!r.ok) throw new Error("marine " + r.status);
  const h = (await r.json()).hourly as { time: string[]; sea_surface_temperature: (number | null)[] } | undefined;
  if (!h?.time) return null;
  let best: { v: number; d: number } | null = null;
  h.time.forEach((s, i) => { const v = h.sea_surface_temperature[i], d = Math.abs(Date.parse(s + ":00Z") - time); if (v != null && (!best || d < best.d)) best = { v, d }; });
  return best ? (best as { v: number }).v : null;
}

/** Water temperature for a place and time: nearby station first, then the sea model, then a farther station. */
export async function fetchWaterTemp(lat: number, lon: number, time: number): Promise<Water> {
  const base = { forLat: lat, forLon: lon, forTime: time, checked: Date.now() };
  const st = nearestStation(lat, lon), round = (v: number) => Math.round(v * 10) / 10;
  const fromStation = async (): Promise<Water | null> => {
    const v = await stationTemp(st, time).catch(() => null);
    return v == null ? null : { ...base, value: round(v), src: "station", station: st.name, km: Math.round(st.km) };
  };
  if (st.km <= NEAR_KM) { const s = await fromStation(); if (s) return s; }
  const sea = await seaTemp(lat, lon, time).catch(() => null);
  if (sea != null) return { ...base, value: round(sea), src: "sea" };
  if (st.km > NEAR_KM && st.km <= FAR_KM) { const s = await fromStation(); if (s) return s; }
  return { ...base, src: "none" };
}

export function needsWater(c: Catch): boolean {
  if (c.kind !== "fish" || !c.loc) return false;
  const w = c.water;
  if (!w) return true;
  if (w.src === "manual") return false;
  // (a value without the place and time it was looked up for counts as not moved)
  const off = (a: number | undefined, b: number, limit: number) => a != null && Math.abs(a - b) > limit;
  const moved = off(w.forLat, c.loc.lat, 0.01) || off(w.forLon, c.loc.lon, 0.01) || off(w.forTime, c.time, 3 * 36e5);
  // stations publish with a delay, so recent catches without data are checked again later
  const retry = w.src === "none" && Date.now() - c.time < 4 * 864e5 && Date.now() - (w.checked ?? 0) > 6 * 36e5;
  return moved || retry;
}
export async function updateWater(c: Catch): Promise<void> {
  const water = await fetchWaterTemp(c.loc!.lat, c.loc!.lon, c.time);
  const fresh = await dbGet("catches", c.id);
  if (!fresh || fresh.water?.src === "manual") return;
  fresh.water = water;
  await dbPut("catches", fresh);
  Object.assign(c, upgrade(fresh));
}

/** Text for the source line under the value. */
export function waterSource(w: Water): string {
  if (w.src === "manual") return t("waterManual");
  if (w.src === "sea") return t("waterSea");
  if (w.src === "station") return t("waterStation", w.station ?? "", w.km ?? 0);
  return "";
}
/** True when the catch has a water temperature to show (narrows c.water.value to a number). */
export const hasWater = (c: Catch): c is Catch & { water: Water & { value: number } } =>
  c.water != null && c.water.value != null && c.water.src !== "none";
